import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { crossBorderOrderService } from "../../../service/admin/crossBorderOrderService";
import { supplierAddressService } from "../../../service/admin/supplierAddressService";
import { ProductsApiService } from "../../../service/website/productsApiService";
import "./style.css";
import { MdWarning } from "react-icons/md";

// Total RMB for items: use order breakdown AP_RMB once per (orderId, productId), else vendorPrice * qty
function getTotalRmbForItems(items) {
  let total = 0;
  const seenOrderProduct = new Set();
  items?.forEach(item => {
    const orderId = item.upsaleoOrderId || item.orderId;
    const productId = item.productId || "";
    const key = `${orderId}\n${productId}`;
    const apRmb = item.apRmbTotal != null && Number(item.apRmbTotal) > 0 ? Number(item.apRmbTotal) : null;
    if (apRmb != null) {
      if (!seenOrderProduct.has(key)) {
        total += apRmb;
        seenOrderProduct.add(key);
      }
    } else {
      const qty = getQuantity(item);
      const unitRmb = Number(item.vendorPrice);
      total += (Number.isFinite(unitRmb) ? unitRmb : 0) * qty;
    }
  });
  return total;
}

// Helper function to group items by order ID
function groupItemsByOrderId(items) {
  const groups = {};
  items?.forEach(item => {
    const orderId = item.upsaleoOrderId || item.orderId;
    if (!groups[orderId]) {
      groups[orderId] = { orderId, variants: [], totalQty: 0, totalRmb: 0 };
    }
    groups[orderId].variants.push(item);
    const qty = getQuantity(item);
    groups[orderId].totalQty += qty;
  });
  // Set totalRmb per group using breakdown AP_RMB when present
  const groupList = Object.values(groups);
  groupList.forEach(g => {
    g.totalRmb = getTotalRmbForItems(g.variants);
  });
  return groupList;
}

// Get unique order count for a supplier
function getUniqueOrderCount(items) {
  const orderIds = new Set();
  items?.forEach(item => {
    const orderId = item.upsaleoOrderId || item.orderId;
    if (orderId) orderIds.add(orderId);
  });
  return orderIds.size;
}

// Get unique SKU count for a supplier
function getUniqueSkuCount(items) {
  const skuIds = new Set();
  items?.forEach(item => {
    const skuId = item.sku || item.skuId;
    if (skuId) skuIds.add(skuId);
  });
  return skuIds.size;
}

// Get quantity: use globalOrderQuantity if available, otherwise use quantity (for both display and calculations)
function getQuantity(item) {
  // Check if globalOrderQuantity exists and is a valid number
  if (item?.globalOrderQuantity !== undefined && item?.globalOrderQuantity !== null) {
    const globalQty = Number(item.globalOrderQuantity);
    if (Number.isFinite(globalQty) && globalQty > 0) {
      return globalQty;
    }
  }
  // Fall back to quantity
  const qty = Number(item?.quantity);
  return Number.isFinite(qty) && qty > 0 ? qty : 1;
}

// Get total quantity for a supplier
function getTotalQuantity(items) {
  return items?.reduce((sum, item) => sum + getQuantity(item), 0) || 0;
}

// Check if quantity was edited (has globalOrderQuantity)
function isEditedQuantity(item) {
  if (item?.isEditedQuantity === true) return true;
  // Check if globalOrderQuantity exists and is a valid number
  if (item?.globalOrderQuantity !== undefined && item?.globalOrderQuantity !== null) {
    const globalQty = Number(item.globalOrderQuantity);
    return Number.isFinite(globalQty) && globalQty > 0;
  }
  return false;
}

// Check if text contains Chinese characters
function containsChinese(text) {
  return /[\u4e00-\u9fa5]/.test(text);
}

export default function CrossBorderOrderSummary() {
  const location = useLocation();
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();

  const [allSuppliers, setAllSuppliers] = useState([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState(new Set());
  const [warehouseAddresses, setWarehouseAddresses] = useState([]);
  const [linkedAddresses, setLinkedAddresses] = useState([]); // Addresses linked to selected warehouse
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null); // For editing address
  const [showWarehouseForm, setShowWarehouseForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [processingSupplier, setProcessingSupplier] = useState(null);
  const [createResults, setCreateResults] = useState(null);
  const [supplierMessages, setSupplierMessages] = useState({});
  const [expandedSuppliers, setExpandedSuppliers] = useState(new Set());
  const [expandedOrders, setExpandedOrders] = useState({});
  const [translatedNames, setTranslatedNames] = useState({});
  const [showAllSuppliers, setShowAllSuppliers] = useState(false);
  const [skippedOrders, setSkippedOrders] = useState([]);
  const [showSkippedDetails, setShowSkippedDetails] = useState(true);
  const [useWarehouseAddress, setUseWarehouseAddress] = useState(true); // Default to warehouse address

  const [newAddress, setNewAddress] = useState({
    fullName: "", mobile: "", phone: "", address: "",
    cityText: "", provinceText: "", areaText: "", townText: "",
    postCode: "", districtCode: "", addressCode: "",
    label: "", isDefault: false,
  });

  const [newWarehouse, setNewWarehouse] = useState({
    label: "", warehousePrefix: "", sequencePattern: "YK", paddingDigits: 4,
    fullName: "", mobile: "", phone: "", address: "",
    cityText: "", provinceText: "", areaText: "", townText: "",
    postCode: "", districtCode: "", addressCode: "",
    source: "warehouse", isDefault: false,
  });

  // Toggle individual supplier expand
  const toggleSupplierExpand = (supplierId) => {
    setExpandedSuppliers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(supplierId)) {
        newSet.delete(supplierId);
      } else {
        newSet.add(supplierId);
      }
      return newSet;
    });
  };

  // Expand/Collapse all suppliers
  const toggleExpandAll = () => {
    if (expandedSuppliers.size === allSuppliers.length) {
      setExpandedSuppliers(new Set());
    } else {
      setExpandedSuppliers(new Set(allSuppliers.map(s => s.sellerOpenId)));
    }
  };

  // Translate Chinese supplier names to English
  const translateSupplierNames = async (suppliers) => {
    const chineseNames = {};
    suppliers.forEach(supplier => {
      const name = supplier.companyName;
      if (name && containsChinese(name) && !translatedNames[name]) {
        chineseNames[name] = name;
      }
    });

    if (Object.keys(chineseNames).length === 0) return;

    try {
      const translateData = { supplierNames: chineseNames };
      const res = await ProductsApiService.translateMandarinToEnglish(translateData, 'en');
      if (res.status?.code === 200 && res.data?.supplierNames) {
        setTranslatedNames(prev => ({ ...prev, ...res.data.supplierNames }));
      }
    } catch (error) {
      console.error("Translation error:", error);
    }
  };

  // Get selected suppliers from navigation state
  useEffect(() => {
    if (location.state?.selectedSuppliers) {
      const suppliers = location.state.selectedSuppliers;
      setAllSuppliers(suppliers);
      setSelectedSupplierIds(new Set(suppliers.map(s => s.sellerOpenId)));
      translateSupplierNames(suppliers);
      if (location.state?.skippedOrders?.length > 0) {
        setSkippedOrders(location.state.skippedOrders);
      }
    } else {
      navigate("/admin/cross-border-orders");
    }
  }, [location.state, navigate]);

  // Fetch warehouse addresses on mount
  useEffect(() => {
    fetchWarehouseAddresses();
  }, []);

  // Fetch addresses linked to selected warehouse when warehouse changes
  useEffect(() => {
    if (selectedWarehouseId) {
      fetchLinkedAddresses(selectedWarehouseId);
    } else {
      setLinkedAddresses([]);
      setSelectedAddressId(null);
    }
  }, [selectedWarehouseId]);

  const fetchLinkedAddresses = async (warehouseId) => {
    try {
      const res = await supplierAddressService.getAddressesByWarehouse(warehouseId);
      if (res.status?.code === 200) {
        setLinkedAddresses(res.data || []);
        // Auto-select default address for this warehouse if exists
        const defaultAddr = res.data?.find(a => a.isDefault);
        if (defaultAddr) {
          setSelectedAddressId(defaultAddr._id);
          setUseWarehouseAddress(false);
        } else {
          // No default linked address, use warehouse address
          setSelectedAddressId(null);
          setUseWarehouseAddress(true);
        }
      }
    } catch (error) {
      console.error("Error fetching linked addresses:", error);
      setLinkedAddresses([]);
      setUseWarehouseAddress(true);
    }
  };

  const fetchWarehouseAddresses = async () => {
    try {
      const res = await supplierAddressService.getWarehouseAddresses();
      if (res.status?.code === 200) {
        // Only show warehouses with prefix YK or SN
        const allowedPrefixes = ["YK", "SN"];
        const filtered = (res.data || []).filter(w =>
          w.warehousePrefix && allowedPrefixes.includes(w.warehousePrefix.toUpperCase())
        );
        setWarehouseAddresses(filtered);
        // Do NOT auto-select — user must choose
      }
    } catch (error) {
      console.error("Error fetching warehouse addresses:", error);
    }
  };

  // Auto-fill order messages when warehouse is selected and suppliers are loaded
  useEffect(() => {
    if (selectedWarehouseId && allSuppliers.length > 0 && Object.keys(supplierMessages).length === 0) {
      const warehouse = warehouseAddresses.find(w => w._id === selectedWarehouseId);
      if (warehouse && warehouse.warehousePrefix) {
        autoFillOrderMessages(warehouse);
      }
    }
  }, [selectedWarehouseId, allSuppliers, warehouseAddresses]);

  // Auto-fill function (silent - no toast notification)
  // Prepends Chinese text: "必须在快递单上备注：" (Must note on express delivery slip:)
  const autoFillOrderMessages = async (warehouse) => {
    const supplierCount = allSuppliers.length;
    if (supplierCount === 0) return;

    try {
      const res = await supplierAddressService.getNextSequences(
        warehouse._id,
        warehouse.warehousePrefix,
        supplierCount,
        warehouse.sequencePattern || "YK",
        warehouse.paddingDigits || 4
      );

      if (res.status?.code === 200 && res.data?.sequences) {
        const sequences = res.data.sequences;
        const newMessages = {};
        const messagePrefix = "必须在快递单上备注：";
        allSuppliers.forEach((supplier, idx) => {
          if (sequences[idx]) {
            newMessages[supplier.sellerOpenId] = `${messagePrefix}${sequences[idx]}`;
          }
        });
        setSupplierMessages(newMessages);
      }
    } catch (error) {
      console.error("Failed to generate order messages:", error);
    }
  };

  // Auto-fill order messages when warehouse is selected
  const handleWarehouseSelect = async (warehouseId) => {
    setSelectedWarehouseId(warehouseId);
    
    if (!warehouseId) {
      return;
    }

    const warehouse = warehouseAddresses.find(w => w._id === warehouseId);
    if (!warehouse) {
      return;
    }
    
    if (!warehouse.warehousePrefix) {
      message.showToast("Please configure warehouse prefix in Warehouse Management first", ToastTypes.WARNING);
      return;
    }

    // Auto-fill order messages
    await autoFillOrderMessages(warehouse);
  };

  // Toggle supplier selection
  const toggleSupplierSelection = (supplierId) => {
    setSelectedSupplierIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(supplierId)) {
        newSet.delete(supplierId);
      } else {
        newSet.add(supplierId);
      }
      return newSet;
    });
  };

  // Select/Deselect all
  const toggleSelectAll = () => {
    if (selectedSupplierIds.size === allSuppliers.length) {
      setSelectedSupplierIds(new Set());
    } else {
      setSelectedSupplierIds(new Set(allSuppliers.map(s => s.sellerOpenId)));
    }
  };

  // Get selected suppliers
  const selectedSuppliers = allSuppliers.filter(s => selectedSupplierIds.has(s.sellerOpenId));

  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewAddress(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleWarehouseChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewWarehouse(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleCreateWarehouse = async () => {
    if (!newWarehouse.label || !newWarehouse.warehousePrefix || !newWarehouse.fullName || 
        !newWarehouse.mobile || !newWarehouse.address || !newWarehouse.provinceText || 
        !newWarehouse.cityText || !newWarehouse.areaText || !newWarehouse.townText ||
        !newWarehouse.postCode || !newWarehouse.districtCode) {
      message.showToast("Please fill all required fields", ToastTypes.ERROR);
      return;
    }
    try {
      loader.showInlineLoader("Creating warehouse...", "small");
      const payload = {
        ...newWarehouse,
        source: "warehouse",
        paddingDigits: parseInt(newWarehouse.paddingDigits) || 4,
      };
      const res = await supplierAddressService.createAddress(payload);
      if (res.status?.code === 201) {
        message.showToast("Warehouse created successfully", ToastTypes.SUCCESS);
        setShowWarehouseForm(false);
        setNewWarehouse({
          label: "", warehousePrefix: "", sequencePattern: "YK", paddingDigits: 4,
          fullName: "", mobile: "", phone: "", address: "",
          cityText: "", provinceText: "", areaText: "", townText: "",
          postCode: "", districtCode: "", addressCode: "",
          source: "warehouse", isDefault: false,
        });
        fetchWarehouseAddresses();
        setSelectedWarehouseId(res.data._id);
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to create warehouse", ToastTypes.ERROR);
    } finally {
      loader.hideLoader();
    }
  };

  const handleCreateAddress = async () => {
    if (!newAddress.fullName || !newAddress.mobile || !newAddress.address ||
        !newAddress.cityText || !newAddress.provinceText || !newAddress.postCode) {
      message.showToast("Please fill all required fields", ToastTypes.ERROR);
      return;
    }
    if (!selectedWarehouseId) {
      message.showToast("Please select a warehouse first", ToastTypes.ERROR);
      return;
    }
    try {
      loader.showInlineLoader("Creating address...", "small");
      // Create address linked to the selected warehouse
      const res = await supplierAddressService.createWarehouseAddress(selectedWarehouseId, newAddress);
      if (res.status?.code === 201) {
        message.showToast("Address created successfully", ToastTypes.SUCCESS);
        setShowAddressForm(false);
        setNewAddress({
          fullName: "", mobile: "", phone: "", address: "",
          cityText: "", provinceText: "", areaText: "", townText: "",
          postCode: "", districtCode: "", addressCode: "",
          label: "", isDefault: false,
        });
        // Refresh linked addresses for this warehouse
        fetchLinkedAddresses(selectedWarehouseId);
        setSelectedAddressId(res.data._id);
        setUseWarehouseAddress(false);
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to create address", ToastTypes.ERROR);
    } finally {
      loader.hideLoader();
    }
  };

  // Set address as primary/default
  const handleSetPrimary = async (addressId) => {
    try {
      loader.showInlineLoader("Setting as primary...", "small");
      const res = await supplierAddressService.setDefaultAddress(addressId);
      if (res.status?.code === 200) {
        message.showToast("Primary address updated", ToastTypes.SUCCESS);
        // Refresh linked addresses for this warehouse
        if (selectedWarehouseId) {
          fetchLinkedAddresses(selectedWarehouseId);
        }
        fetchWarehouseAddresses();
      }
    } catch (error) {
      message.showToast("Failed to set primary address", ToastTypes.ERROR);
    } finally {
      loader.hideLoader();
    }
  };

  // Edit address - populate form with existing data
  const handleEditAddress = (addr) => {
    setEditingAddressId(addr._id);
    setNewAddress({
      fullName: addr.fullName || "",
      mobile: addr.mobile || "",
      phone: addr.phone || "",
      address: addr.address || "",
      cityText: addr.cityText || "",
      provinceText: addr.provinceText || "",
      areaText: addr.areaText || "",
      townText: addr.townText || "",
      postCode: addr.postCode || "",
      districtCode: addr.districtCode || "",
      addressCode: addr.addressCode || "",
      label: addr.label || "",
      isDefault: addr.isDefault || false,
    });
    setShowAddressForm(true);
  };

  // Update existing address
  const handleUpdateAddress = async () => {
    if (!newAddress.fullName || !newAddress.mobile || !newAddress.address ||
        !newAddress.cityText || !newAddress.provinceText || !newAddress.postCode) {
      message.showToast("Please fill all required fields", ToastTypes.ERROR);
      return;
    }
    try {
      loader.showInlineLoader("Updating address...", "small");
      const res = await supplierAddressService.updateAddress(editingAddressId, newAddress);
      if (res.status?.code === 200) {
        message.showToast("Address updated successfully", ToastTypes.SUCCESS);
        setShowAddressForm(false);
        setEditingAddressId(null);
        setNewAddress({
          fullName: "", mobile: "", phone: "", address: "",
          cityText: "", provinceText: "", areaText: "", townText: "",
          postCode: "", districtCode: "", addressCode: "",
          label: "", isDefault: false,
        });
        // Refresh linked addresses for this warehouse
        if (selectedWarehouseId) {
          fetchLinkedAddresses(selectedWarehouseId);
        }
        fetchWarehouseAddresses();
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to update address", ToastTypes.ERROR);
    } finally {
      loader.hideLoader();
    }
  };

  // Cancel edit mode
  const handleCancelAddressForm = () => {
    setShowAddressForm(false);
    setEditingAddressId(null);
    setNewAddress({
      fullName: "", mobile: "", phone: "", address: "",
      cityText: "", provinceText: "", areaText: "", townText: "",
      postCode: "", districtCode: "", addressCode: "",
      label: "", isDefault: false,
    });
  };

  // Process single supplier
  const handleProcessSingleSupplier = async (supplier) => {
    // Validate warehouse selection
    if (!selectedWarehouseId) {
      message.showToast("Please select a source warehouse (YK or SN)", ToastTypes.ERROR);
      return;
    }
    // Determine which address to use
    let addressSource;
    if (useWarehouseAddress) {
      addressSource = selectedWarehouse;
    } else {
      addressSource = linkedAddresses.find(a => a._id === selectedAddressId);
    }
    
    if (!addressSource) {
      message.showToast("Please select a shipping address", ToastTypes.ERROR);
      return;
    }
    if (!supplierMessages[supplier.sellerOpenId]?.trim()) {
      message.showToast("Please enter order message for this supplier", ToastTypes.ERROR);
      return;
    }

    const addressParam = {
      fullName: addressSource.fullName,
      mobile: addressSource.mobile,
      phone: addressSource.phone || "",
      address: addressSource.address,
      cityText: addressSource.cityText,
      provinceText: addressSource.provinceText,
      areaText: addressSource.areaText || "",
      townText: addressSource.townText || "",
      postCode: addressSource.postCode,
      districtCode: addressSource.districtCode || "",
      addressCode: addressSource.addressCode || "",
    };

    const supplierWithMessage = {
      ...supplier,
      message: supplierMessages[supplier.sellerOpenId]?.trim() || "",
    };

    setProcessingSupplier(supplier.sellerOpenId);
    try {
      const res = await crossBorderOrderService.createCrossBorderOrders({
        supplierGroups: [supplierWithMessage],
        addressParam,
        userId: addressSource.createdBy || addressSource.userId || null,
        supplierMessages: { [supplier.sellerOpenId]: supplierMessages[supplier.sellerOpenId] },
        warehouseCode: selectedWarehouse?.warehousePrefix || '',
      });

      if (res.status?.code === 200) {
        const successCount = res.data.summary?.successCount ?? res.data.successCount ?? 0;
        const failCount = res.data.summary?.failCount ?? res.data.failCount ?? 0;
        const totalAttempted = successCount + failCount;
        
        // Commit sequence for this single supplier (including failed) to reserve sequence number
        if (selectedWarehouseId && totalAttempted > 0) {
          try {
            await supplierAddressService.commitSequences(selectedWarehouseId, totalAttempted);
          } catch (commitError) {
            console.error("Failed to commit sequence:", commitError);
          }
        }

        if (failCount > 0 && successCount === 0) {
          // All failed — show error details and keep supplier in list
          const failedResult = res.data.results?.find(r => !r.success);
          const errorMsg = failedResult?.error || "Order creation failed on 1688 API";
          message.showToast(`${supplier.companyName}: ${errorMsg}`, ToastTypes.ERROR);
        } else {
          if (failCount > 0) {
            message.showToast(`${supplier.companyName}: ${successCount} created, ${failCount} failed`, ToastTypes.WARNING);
          } else {
            message.showToast(`Processed ${supplier.companyName}: ${successCount} order(s) created`, ToastTypes.SUCCESS);
          }
          setAllSuppliers(prev => prev.filter(s => s.sellerOpenId !== supplier.sellerOpenId));
          setSelectedSupplierIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(supplier.sellerOpenId);
            return newSet;
          });
        }
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to process supplier", ToastTypes.ERROR);
    } finally {
      setProcessingSupplier(null);
    }
  };

  // Process all selected suppliers
  const handleConfirmOrder = async () => {
    // Validate warehouse selection
    if (!selectedWarehouseId) {
      message.showToast("Please select a source warehouse (YK or SN)", ToastTypes.ERROR);
      return;
    }
    // Determine which address to use
    let addressSource;
    if (useWarehouseAddress) {
      addressSource = selectedWarehouse;
    } else {
      addressSource = linkedAddresses.find(a => a._id === selectedAddressId);
    }
    
    if (!addressSource) {
      message.showToast("Please select a shipping address", ToastTypes.ERROR);
      return;
    }
    if (selectedSuppliers.length === 0) {
      message.showToast("Please select at least one supplier", ToastTypes.ERROR);
      return;
    }

    const missingMessages = selectedSuppliers.filter(s => !supplierMessages[s.sellerOpenId]?.trim());
    if (missingMessages.length > 0) {
      message.showToast("Please enter order message for all selected suppliers", ToastTypes.ERROR);
      return;
    }

    const addressParam = {
      fullName: addressSource.fullName,
      mobile: addressSource.mobile,
      phone: addressSource.phone || "",
      address: addressSource.address,
      cityText: addressSource.cityText,
      provinceText: addressSource.provinceText,
      areaText: addressSource.areaText || "",
      townText: addressSource.townText || "",
      postCode: addressSource.postCode,
      districtCode: addressSource.districtCode || "",
      addressCode: addressSource.addressCode || "",
    };

    const suppliersWithMessages = selectedSuppliers.map(s => ({
      ...s,
      message: supplierMessages[s.sellerOpenId]?.trim() || "",
    }));

    setIsCreating(true);
    try {
      const res = await crossBorderOrderService.createCrossBorderOrders({
        supplierGroups: suppliersWithMessages,
        addressParam,
        userId: addressSource.createdBy || addressSource.userId || null,
        supplierMessages,
        warehouseCode: selectedWarehouse?.warehousePrefix || '',
      });

      if (res.status?.code === 200) {
        const successCount = res.data.summary?.successCount ?? res.data.successCount ?? 0;
        const failCount = res.data.summary?.failCount ?? res.data.failCount ?? 0;
        const totalAttempted = successCount + failCount;
        
        // Commit sequences for ALL attempted orders (including failed) to reserve sequence numbers
        if (selectedWarehouseId && totalAttempted > 0) {
          try {
            await supplierAddressService.commitSequences(selectedWarehouseId, totalAttempted);
          } catch (commitError) {
            console.error("Failed to commit sequences:", commitError);
          }
        }
        
        message.showToast(
          `Created ${successCount} orders, ${failCount} failed`,
          failCount > 0 ? ToastTypes.WARNING : ToastTypes.SUCCESS
        );
        setCreateResults({ ...res.data, successCount, failCount });
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to create orders", ToastTypes.ERROR);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSupplierMessageChange = (supplierId, value) => {
    setSupplierMessages(prev => ({ ...prev, [supplierId]: value }));
  };

  // Calculate totals for selected suppliers only
  const totalSkus = selectedSuppliers.reduce((sum, s) => sum + getUniqueSkuCount(s.items), 0);
  const totalOrders = selectedSuppliers.reduce((sum, s) => sum + getUniqueOrderCount(s.items), 0);
  const totalQty = selectedSuppliers.reduce((sum, s) => sum + getTotalQuantity(s.items), 0); // Use globalOrderQuantity if available, otherwise quantity
  const hasEditedQty = selectedSuppliers.some(s => s.items?.some(isEditedQuantity));
  // Results page
  if (createResults) {
    return (
      <div className="cb-container">
        <div className="cb-header">
          <h1>Order Creation Results</h1>
        </div>
        <div className="cb-content">
          <div className="cb-results-page">
            <div className="cb-results-stats-large">
              <div className="stat-box success">
                <i className="fas fa-check-circle"></i>
                <span className="stat-num">{createResults.successCount}</span>
                <span className="stat-label">Successful</span>
              </div>
              <div className="stat-box failed">
                <i className="fas fa-times-circle"></i>
                <span className="stat-num">{createResults.failCount}</span>
                <span className="stat-label">Failed</span>
              </div>
            </div>
            <div className="cb-results-details">
              <h3>Order Details</h3>
              {createResults.results?.map((result, idx) => (
                <div key={idx} className={`cb-result-card ${result.success ? "success" : "error"}`}>
                  <div className="result-header">
                    <span className="result-supplier">{result.supplier}</span>
                    <span className={`result-status ${result.success ? "ok" : "err"}`}>
                      {result.success ? "Success" : "Failed"}
                    </span>
                  </div>
                  {result.success ? (
                    <div className="result-body">
                      <p><strong>Global Order IDs:</strong> {result.alibabaOrderIds?.join(", ") || "-"}</p>
                      <p><strong>Internal ID:</strong> {result.crossBorderOrderId}</p>
                      <p><strong>Items:</strong> {result.itemCount} SKUs</p>
                    </div>
                  ) : (
                    <div className="result-body error">
                      <p><strong>Error:</strong> {result.error}</p>
                      {result.statusCode && <p><strong>Status Code:</strong> {result.statusCode}</p>}
                      {result.apiResponse && (
                        <details style={{marginTop: '8px'}}>
                          <summary style={{cursor: 'pointer', color: '#666', fontSize: '12px'}}>View API Response</summary>
                          <pre style={{fontSize: '11px', background: '#f5f5f5', padding: '8px', borderRadius: '4px', overflow: 'auto', maxHeight: '200px', marginTop: '4px'}}>
                            {JSON.stringify(result.apiResponse, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="cb-actions-bottom">
              <button className="cb-btn secondary" onClick={() => navigate("/admin/cross-border-orders")}>
                <i className="fas fa-arrow-left"></i> Back to Orders
              </button>
              <button className="cb-btn primary" onClick={() => {
                setCreateResults(null);
                navigate("/admin/cross-border-orders", { state: { tab: "placed" } });
              }}>
                View Placed Orders <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const selectedWarehouse = warehouseAddresses.find(w => w._id === selectedWarehouseId);

  return (
    <div className="cb-container">
      <div className="cb-header">
        <h1>Order Summary & Confirmation</h1>
        <p>Review your order details and select shipping address before confirming</p>
      </div>

      <div className="cb-content">
        <div className="cb-summary-grid">
          {/* Left: Order Summary */}
          <div className="cb-summary-left">
            <div className="cb-summary-card">
              <h3><i className="fas fa-shopping-cart"></i> Order Summary</h3>
              
              <div className="cb-summary-stats">
                <div className="summary-stat">
                  <span className="stat-value">{selectedSuppliers.length}</span>
                  <span className="stat-label">Suppliers</span>
                </div>
                <div className="summary-stat">
                  <span className="stat-value">{totalSkus}</span>
                  <span className="stat-label">Total SKUs</span>
                </div>
                <div className="summary-stat">
                  <span className="stat-value">{totalOrders}</span>
                  <span className="stat-label">Orders</span>
                </div>
                <div className="summary-stat">
                  <span className="stat-value">
                    {totalQty}{hasEditedQty ? " (edited)" : ""}
                  </span>
                  <span className="stat-label">Total Qty</span>
                </div>
              </div>

              {/* Skipped Orders Error Box */}
              {skippedOrders.length > 0 && (
                <div className="cb-skipped-orders-box">
                  <div className="cb-skipped-header" onClick={() => setShowSkippedDetails(!showSkippedDetails)}>
                    <div className="cb-skipped-title">
                      <MdWarning className="cb-skipped-icon" />
                      <span>{skippedOrders.length} order(s) skipped — supplier not found</span>
                    </div>
                    <i className={`fas fa-chevron-${showSkippedDetails ? 'up' : 'down'}`}></i>
                  </div>
                  {showSkippedDetails && (
                    <div className="cb-skipped-details">
                      {skippedOrders.map((order, idx) => (
                        <div key={idx} className="cb-skipped-order-row">
                          <div className="cb-skipped-order-id">
                            <span className="skipped-label">Order:</span>
                            <span className="skipped-value">{order.upsaleoOrderId || order.orderId}</span>
                          </div>
                          {order.skus?.length > 0 && (
                            <div className="cb-skipped-skus">
                              <span className="skipped-label">SKUs:</span>
                              <span className="skipped-value">{order.skus.join(", ")}</span>
                            </div>
                          )}
                          <div className="cb-skipped-reason">
                            <span className="skipped-label">Reason:</span>
                            <span className="skipped-value">{order.reason}</span>
                          </div>
                        </div>
                      ))}
                      <div className="cb-skipped-hint">
                        These orders are highlighted in yellow on the Order Management page. Please map the supplier (sellerOpenId) for these products before retrying.
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="cb-supplier-list-summary">
                <div className="supplier-list-header">
                  <h4>Selected Suppliers</h4>
                  <div className="header-actions">
                    <button 
                      className="toggle-scroll-btn"
                      onClick={() => setShowAllSuppliers(!showAllSuppliers)}
                      type="button"
                    >
                      {showAllSuppliers ? (
                        <><i className="fas fa-compress-arrows-alt"></i> Scroll</>
                      ) : (
                        <><i className="fas fa-expand-arrows-alt"></i> Show All</>
                      )}
                    </button>
                    <button 
                      className="expand-all-btn"
                      onClick={toggleExpandAll}
                      type="button"
                    >
                      {expandedSuppliers.size === allSuppliers.length ? (
                        <><i className="fas fa-compress-alt"></i> Collapse</>
                      ) : (
                        <><i className="fas fa-expand-alt"></i> Expand</>
                      )}
                    </button>
                    <label className="select-all-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedSupplierIds.size === allSuppliers.length && allSuppliers.length > 0}
                        onChange={toggleSelectAll}
                      />
                      <span>All ({selectedSupplierIds.size}/{allSuppliers.length})</span>
                    </label>
                  </div>
                </div>

                <div className={`supplier-blocks-container ${showAllSuppliers ? 'show-all' : 'with-scroll'}`}>
                {/* Header Row */}
                <div className="supplier-list-table-header">
                  <div className="header-checkbox"></div>
                  <div className="header-supplier">Supplier</div>
                  <div className="header-stats">Stats</div>
                  <div className="header-message">Order Message (快递单备注)</div>
                  <div className="header-price">Total</div>
                  <div className="header-action">Action</div>
                </div>
                
                {allSuppliers.map((supplier, idx) => {
                  const isSelected = selectedSupplierIds.has(supplier.sellerOpenId);
                  const isExpanded = expandedSuppliers.has(supplier.sellerOpenId);
                  const supplierTotalRMB = getTotalRmbForItems(supplier.items);
                  const groupedOrders = groupItemsByOrderId(supplier.items);
                  const uniqueOrderCount = getUniqueOrderCount(supplier.items);
                  const isProcessing = processingSupplier === supplier.sellerOpenId;
                  
                  const toggleOrderExpand = (orderId) => {
                    setExpandedOrders(prev => ({ ...prev, [orderId]: !prev[orderId] }));
                  };

                  return (
                    <div key={idx} className={`supplier-block ${!isSelected ? 'unselected' : ''}`}>
                      <div className={`supplier-row clickable ${isExpanded ? 'expanded' : ''}`}>
                        <div className="supplier-checkbox" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSupplierSelection(supplier.sellerOpenId)}
                          />
                        </div>

                        <div className="supplier-info" onClick={() => toggleSupplierExpand(supplier.sellerOpenId)}>
                          <div className="supplier-name-dual">
                            <span className="supplier-name-en">
                              {translatedNames[supplier.companyName] || supplier.companyNameEn || supplier.companyName || "Unknown"}
                            </span>
                            <span className="supplier-name-cn">{supplier.companyName || ""}</span>
                          </div>
                          <span className="supplier-id">{supplier.sellerOpenId}</span>
                        </div>

                        <div className="supplier-stats-combined">
                          <span className="stat-item">{uniqueOrderCount} Orders</span>
                          <span className="stat-item">{getUniqueSkuCount(supplier.items)} SKUs</span>
                          <span className="stat-item">
                            {supplier.items?.reduce((sum, item) => sum + getQuantity(item), 0) || 0} Qty{supplier.items?.some(isEditedQuantity) ? " (edited)" : ""}
                          </span>
                        </div>

                        <input
                          type="text"
                          placeholder="Order message"
                          value={supplierMessages[supplier.sellerOpenId] || ""}
                          onChange={(e) => handleSupplierMessageChange(supplier.sellerOpenId, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className={`supplier-msg-input ${!supplierMessages[supplier.sellerOpenId]?.trim() ? "input-error" : ""}`}
                          disabled={!isSelected}
                        />

                        <span className="rmb-price-theme">¥{supplierTotalRMB.toFixed(2)}</span>

                        <button
                          className="cb-btn-sm primary process-single-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProcessSingleSupplier(supplier);
                          }}
                          disabled={!isSelected || isProcessing || !supplierMessages[supplier.sellerOpenId]?.trim()}
                        >
                          {isProcessing ? "..." : "Process"}
                        </button>

                        <i 
                          className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} expand-icon`}
                          onClick={() => toggleSupplierExpand(supplier.sellerOpenId)}
                        ></i>
                      </div>

                      {isExpanded && (
                        <div className="supplier-items-detail">
                          <div className="supplier-items-scroll">
                          <div className="cb-order-groups">
                            {groupedOrders.map((orderGroup, oIdx) => (
                              <div key={oIdx} className="cb-order-group">
                                <div 
                                  className={`cb-order-group-header ${expandedOrders[orderGroup.orderId] ? 'expanded' : ''}`}
                                  onClick={() => toggleOrderExpand(orderGroup.orderId)}
                                >
                                  <div className="order-group-left">
                                    <i className={`fas fa-chevron-${expandedOrders[orderGroup.orderId] ? 'down' : 'right'} expand-arrow`}></i>
                                    <span className="order-id-link">{orderGroup.orderId}</span>
                                    <span className="variant-count">{orderGroup.variants.length} variant(s)</span>
                                  </div>
                                  <div className="order-group-right">
                                    <span className="qty-badge">{orderGroup.totalQty} qty</span>
                                    <span className="rmb-price-cell">¥{orderGroup.totalRmb.toFixed(2)}</span>
                                  </div>
                                </div>
                                
                                {expandedOrders[orderGroup.orderId] && (
                                  <div className="cb-order-variants">
                                    <table className="cb-variant-table">
                                      <thead>
                                        <tr>
                                          <th style={{width: '50px'}}>Image</th>
                                          <th style={{width: '120px'}}>SKU ID</th>
                                          <th style={{width: '180px'}}>Variant</th>
                                          <th style={{width: '160px'}}>Spec ID</th>
                                          <th style={{width: '50px'}}>Qty</th>
                                          <th style={{width: '80px'}}>Unit</th>
                                          <th style={{width: '80px'}}>Total</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {orderGroup.variants.map((item, vIdx) => {
                                          let variantDisplay = "-";
                                          if (item.skuAttributes && item.skuAttributes.length > 0) {
                                            variantDisplay = item.skuAttributes.map(attr => attr.valueTrans || attr.value).filter(v => v).join(" / ");
                                          } else if (item.variantName) {
                                            variantDisplay = item.variantName;
                                          } else if (item.attributes) {
                                            variantDisplay = item.attributes;
                                          }
                                          const unitPrice = Number(item.vendorPrice);
                                          const unitRmb = Number.isFinite(unitPrice) ? unitPrice : 0;
                                          const totalPrice = unitRmb * getQuantity(item);
                                          
                                          return (
                                            <tr key={vIdx}>
                                              <td>{item.image ? <img src={item.image} alt="" className="item-thumb-sm" /> : <span>-</span>}</td>
                                              <td><span className="sku-code">{item.sku || "-"}</span></td>
                                              <td><span className="variant-text">{variantDisplay}</span></td>
                                              <td><span className={`spec-id-full${!item.specId ? ' spec-id-missing' : ''}`} title={item.specId || "Missing specId - order may fail"}>{item.specId || <span style={{color: '#e74c3c', fontWeight: 'bold'}}>⚠ Missing</span>}</span></td>
                                              <td><span className="qty-badge">{getQuantity(item)}</span></td>
                                              <td><span className="price-rmb">¥{unitRmb.toFixed(2)}</span></td>
                                              <td><span className="price-rmb-total">¥{totalPrice.toFixed(2)}</span></td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          </div>
                          <div className="supplier-total-row">
                            <span>Total:</span>
                            <span className="price-rmb-total">¥{supplierTotalRMB.toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>

              {/* Grand Total + Process Button */}
              <div className="cb-grand-total-section">
                <div className="cb-grand-total-theme">
                  <span>Estimated Total (RMB):</span>
                  <span className="grand-total-price-theme">
                    ¥{selectedSuppliers.reduce((sum, s) => sum + getTotalRmbForItems(s.items), 0).toFixed(2)}
                  </span>
                </div>
                <button
                  className="cb-btn primary process-all-btn"
                  onClick={handleConfirmOrder}
                  disabled={!selectedWarehouseId || (!selectedWarehouse && !selectedAddressId) || selectedSuppliers.length === 0 || selectedSuppliers.some(s => !supplierMessages[s.sellerOpenId]?.trim()) || isCreating}
                >
                  {isCreating ? "Creating..." : `Process Selected (${selectedSuppliers.length})`}
                </button>
              </div>
            </div>
          </div>

          {/* Right: Warehouse & Address Selection */}
          <div className="cb-summary-right">
            {/* Source Warehouse Selection */}
            <div className="cb-warehouse-card">
              <div className="cb-warehouse-header">
                <h3><i className="fas fa-warehouse"></i> Source Warehouse</h3>
                <button className="cb-btn-sm primary" onClick={() => setShowWarehouseForm(!showWarehouseForm)}>
                  <i className="fas fa-plus"></i> New
                </button>
              </div>
              <div className="warehouse-selector">
                <select 
                  value={selectedWarehouseId || ""} 
                  onChange={(e) => handleWarehouseSelect(e.target.value)}
                  className={`warehouse-dropdown${!selectedWarehouseId ? " warehouse-required" : ""}`}
                >
                  <option value="">Select Warehouse</option>
                  {warehouseAddresses.map(wh => (
                    <option key={wh._id} value={wh._id}>
                      {wh.label || wh.fullName} ({wh.warehousePrefix})
                    </option>
                  ))}
                </select>
                {!selectedWarehouseId && (
                  <span className="warehouse-validation-msg">
                    <i className="fas fa-exclamation-circle"></i> Please select a source warehouse
                  </span>
                )}
              </div>

              {/* New Warehouse Form */}
              {showWarehouseForm && (
                <div className="cb-new-warehouse-form">
                  <div className="form-row-compact">
                    <div className="form-field-sm">
                      <label>Name *</label>
                      <input name="label" value={newWarehouse.label} onChange={handleWarehouseChange} placeholder="Main Warehouse" required />
                    </div>
                    <div className="form-field-sm">
                      <label>Prefix *</label>
                      <input name="warehousePrefix" value={newWarehouse.warehousePrefix} onChange={handleWarehouseChange} placeholder="WH1" required />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <div className="form-field-sm">
                      <label>Pattern (Auto)</label>
                      <input 
                        value={`${newWarehouse.warehousePrefix || "?"}${String(1).padStart(newWarehouse.paddingDigits || 4, '0')}`}
                        readOnly
                        className="readonly-input"
                      />
                    </div>
                    <div className="form-field-xs">
                      <label>Digits *</label>
                      <input type="number" name="paddingDigits" value={newWarehouse.paddingDigits} onChange={handleWarehouseChange} min="1" max="8" required />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <div className="form-field-sm">
                      <label>Contact *</label>
                      <input name="fullName" value={newWarehouse.fullName} onChange={handleWarehouseChange} placeholder="Zhang San" required />
                    </div>
                    <div className="form-field-sm">
                      <label>Mobile *</label>
                      <input name="mobile" value={newWarehouse.mobile} onChange={handleWarehouseChange} placeholder="15251667788" required />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <div className="form-field-full">
                      <label>Address *</label>
                      <input name="address" value={newWarehouse.address} onChange={handleWarehouseChange} placeholder="No. 699, Wangshang Road" required />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <div className="form-field-sm">
                      <label>Province *</label>
                      <input name="provinceText" value={newWarehouse.provinceText} onChange={handleWarehouseChange} placeholder="Zhejiang" required />
                    </div>
                    <div className="form-field-sm">
                      <label>City *</label>
                      <input name="cityText" value={newWarehouse.cityText} onChange={handleWarehouseChange} placeholder="Hangzhou" required />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <div className="form-field-sm">
                      <label>Area *</label>
                      <input name="areaText" value={newWarehouse.areaText} onChange={handleWarehouseChange} placeholder="Binjiang" required />
                    </div>
                    <div className="form-field-sm">
                      <label>Town *</label>
                      <input name="townText" value={newWarehouse.townText} onChange={handleWarehouseChange} placeholder="Changhe" required />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <div className="form-field-sm">
                      <label>Post Code *</label>
                      <input name="postCode" value={newWarehouse.postCode} onChange={handleWarehouseChange} placeholder="310000" required />
                    </div>
                    <div className="form-field-sm">
                      <label>District Code *</label>
                      <input name="districtCode" value={newWarehouse.districtCode} onChange={handleWarehouseChange} placeholder="310107" required />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <label className="checkbox-label-sm">
                      <input type="checkbox" name="isDefault" checked={newWarehouse.isDefault} onChange={handleWarehouseChange} />
                      Set as default
                    </label>
                  </div>
                  <div className="form-actions-compact">
                    <button className="cb-btn-sm secondary" onClick={() => setShowWarehouseForm(false)}>Cancel</button>
                    <button className="cb-btn-sm primary" onClick={handleCreateWarehouse}>Save</button>
                  </div>
                </div>
              )}
              
              {selectedWarehouse && (
                <div className="selected-warehouse-details compact">
                  <div className="wh-info-grid">
                    <div className="wh-info-item">
                      <span className="wh-info-label">Prefix</span>
                      <span className="wh-info-value prefix">{selectedWarehouse.warehousePrefix || "N/A"}</span>
                    </div>
                    <div className="wh-info-item">
                      <span className="wh-info-label">Pattern</span>
                      <span className="wh-info-value">{selectedWarehouse.warehousePrefix || "?"}{String(1).padStart(selectedWarehouse.paddingDigits || 4, '0')}</span>
                    </div>
                  </div>
                  <div className="wh-address-line">
                    <i className="fas fa-map-marker-alt"></i>
                    <span>{selectedWarehouse.address}, {selectedWarehouse.cityText}</span>
                  </div>
                  <div className="wh-contact-line">
                    <i className="fas fa-user"></i>
                    <span>{selectedWarehouse.fullName} - {selectedWarehouse.mobile}</span>
                  </div>
                  {selectedWarehouse.isDefault && (
                    <span className="primary-badge-sm"><i className="fas fa-star"></i> Primary</span>
                  )}
                  {!selectedWarehouse.warehousePrefix && (
                    <div className="wh-warning">
                      <a href="/admin/warehouse-management" target="_blank"><i className="fas fa-cog"></i> Configure Prefix</a>
                    </div>
                  )}
                </div>
              )}
              {!showWarehouseForm && warehouseAddresses.length === 0 && (
                <div className="no-warehouse-box">
                  <p>No warehouses found</p>
                  <button className="cb-btn-sm primary" onClick={() => setShowWarehouseForm(true)}>
                    <i className="fas fa-plus"></i> Add Warehouse
                  </button>
                </div>
              )}
            </div>

            {/* Shipping Address - Only show when warehouse is selected */}
            {selectedWarehouseId && selectedWarehouse && (
            <div className="cb-address-card">
              <div className="cb-address-header">
                <h3><i className="fas fa-map-marker-alt"></i> Shipping Address</h3>
                <button className="cb-btn-sm primary" onClick={() => setShowAddressForm(!showAddressForm)}>
                  <i className="fas fa-plus"></i> New
                </button>
              </div>

              {/* Address Form - Add or Edit mode */}
              {showAddressForm && (
                <div className="cb-new-address-form compact">
                  <div className="form-title">
                    {editingAddressId ? <><i className="fas fa-edit"></i> Edit Address</> : <><i className="fas fa-plus"></i> New Address</>}
                  </div>
                  <div className="form-row-compact">
                    <div className="form-field-full">
                      <label>Label</label>
                      <input name="label" value={newAddress.label} onChange={handleAddressChange} placeholder="e.g., Office, Branch 2" />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <div className="form-field-sm">
                      <label>Name *</label>
                      <input name="fullName" value={newAddress.fullName} onChange={handleAddressChange} placeholder="Zhang San" />
                    </div>
                    <div className="form-field-sm">
                      <label>Mobile *</label>
                      <input name="mobile" value={newAddress.mobile} onChange={handleAddressChange} placeholder="15251667788" />
                    </div>
                    <div className="form-field-sm">
                      <label>Phone</label>
                      <input name="phone" value={newAddress.phone} onChange={handleAddressChange} placeholder="0517-889900" />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <div className="form-field-full">
                      <label>Address *</label>
                      <input name="address" value={newAddress.address} onChange={handleAddressChange} placeholder="No. 699, Wangshang Road" />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <div className="form-field-sm">
                      <label>Province *</label>
                      <input name="provinceText" value={newAddress.provinceText} onChange={handleAddressChange} placeholder="Zhejiang" />
                    </div>
                    <div className="form-field-sm">
                      <label>City *</label>
                      <input name="cityText" value={newAddress.cityText} onChange={handleAddressChange} placeholder="Hangzhou" />
                    </div>
                    <div className="form-field-sm">
                      <label>Area</label>
                      <input name="areaText" value={newAddress.areaText} onChange={handleAddressChange} placeholder="Binjiang" />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <div className="form-field-sm">
                      <label>Town</label>
                      <input name="townText" value={newAddress.townText} onChange={handleAddressChange} placeholder="Changhe" />
                    </div>
                    <div className="form-field-sm">
                      <label>Post Code *</label>
                      <input name="postCode" value={newAddress.postCode} onChange={handleAddressChange} placeholder="310000" />
                    </div>
                    <div className="form-field-sm">
                      <label>District Code</label>
                      <input name="districtCode" value={newAddress.districtCode} onChange={handleAddressChange} placeholder="310107" />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <label className="checkbox-label-sm">
                      <input type="checkbox" name="isDefault" checked={newAddress.isDefault} onChange={handleAddressChange} />
                      Set as default for this warehouse
                    </label>
                  </div>
                  <div className="form-actions-compact">
                    <button className="cb-btn-sm secondary" onClick={handleCancelAddressForm}>Cancel</button>
                    <button className="cb-btn-sm primary" onClick={editingAddressId ? handleUpdateAddress : handleCreateAddress}>
                      {editingAddressId ? "Update" : "Save"}
                    </button>
                  </div>
                </div>
              )}

              {/* Address List - Default on top, then others */}
              <div className="cb-address-list">
                {(() => {
                  // Combine warehouse address with linked addresses
                  // Mark warehouse address explicitly, linked addresses should not have isWarehouse
                  const allAddresses = [
                    { ...selectedWarehouse, isWarehouse: true },
                    ...linkedAddresses.map(addr => ({ ...addr, isWarehouse: false }))
                  ];
                  
                  // Sort: default first, then by creation date
                  const sortedAddresses = allAddresses.sort((a, b) => {
                    if (a.isDefault && !b.isDefault) return -1;
                    if (!a.isDefault && b.isDefault) return 1;
                    return 0;
                  });
                  
                  return sortedAddresses.map((addr, idx) => {
                    const isWarehouse = addr.isWarehouse;
                    const isSelected = isWarehouse 
                      ? useWarehouseAddress 
                      : (!useWarehouseAddress && selectedAddressId === addr._id);
                    
                    return (
                      <div
                        key={addr._id}
                        className={`address-item ${isSelected ? "selected" : ""} ${isWarehouse ? "warehouse-address" : ""}`}
                        onClick={() => {
                          if (isWarehouse) {
                            setUseWarehouseAddress(true);
                            setSelectedAddressId(null);
                          } else {
                            setUseWarehouseAddress(false);
                            setSelectedAddressId(addr._id);
                          }
                        }}
                      >
                        <div className="address-radio">
                          <input 
                            type="radio" 
                            checked={isSelected} 
                            onChange={() => {
                              if (isWarehouse) {
                                setUseWarehouseAddress(true);
                                setSelectedAddressId(null);
                              } else {
                                setUseWarehouseAddress(false);
                                setSelectedAddressId(addr._id);
                              }
                            }} 
                          />
                        </div>
                        <div className="address-content">
                          <div className="address-top">
                            <span className="address-name">{addr.fullName}</span>
                            <span className="address-phone">{addr.mobile}</span>
                            {isWarehouse && <span className="warehouse-tag"><i className="fas fa-warehouse"></i> Warehouse</span>}
                            {addr.isDefault && <span className="default-tag">Default</span>}
                            {addr.label && <span className="label-tag">{addr.label}</span>}
                          </div>
                          <div className="address-detail">
                            {addr.address}
                            {addr.townText && `, ${addr.townText}`}
                            {addr.areaText && `, ${addr.areaText}`}
                            , {addr.cityText}, {addr.provinceText}
                            {addr.postCode && `, ${addr.postCode}`}
                          </div>
                          {addr.phone && (
                            <div className="address-phone-alt">
                              <i className="fas fa-phone-alt"></i> {addr.phone}
                            </div>
                          )}
                          {/* Show actions for all linked addresses (not warehouse) */}
                          {!isWarehouse && (
                            <div className="address-actions">
                              {!addr.isDefault && (
                                <button 
                                  className="set-primary-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSetPrimary(addr._id);
                                  }}
                                >
                                  <i className="fas fa-star"></i> Set as Default
                                </button>
                              )}
                              <button 
                                className="edit-address-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditAddress(addr);
                                }}
                              >
                                <i className="fas fa-edit"></i> Edit
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
                
                {linkedAddresses.length === 0 && (
                  <div className="no-other-addresses">
                    <span>No additional addresses for this warehouse</span>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Message when no warehouse selected */}
            {!selectedWarehouseId && (
              <div className="select-warehouse-hint">
                <i className="fas fa-info-circle"></i>
                <p>Please select a warehouse first to view shipping addresses</p>
              </div>
            )}
          </div>
        </div>

        {/* Back Button Only */}
        <div className="cb-back-action">
          <button className="cb-btn secondary" onClick={() => navigate("/admin/cross-border-orders")}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>
      </div>
    </div>
  );
}
